import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "../ui/textarea";
import { useCreateDocument } from "@/api/document/documents";
import {
  appendNoDuplicateRecordNumberRefine,
  documentSchema,
} from "@/api/document/document-validation";

const schema = appendNoDuplicateRecordNumberRefine(
  documentSchema.pick({
    documentsDescriptionNumeroEnregistrement: true,
    documentsDescriptionTitre: true,
  }),
);
type FormType = z.infer<typeof schema>;

type NewDocumentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewDocumentDialog = ({ onOpenChange, open }: NewDocumentDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: {
      documentsDescriptionNumeroEnregistrement: "",
      documentsDescriptionTitre: "",
    },
  });
  const { mutateAsync } = useCreateDocument();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const result = await mutateAsync(data);
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      navigate(`/edit/document/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("documentsDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createDdocument")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="documentsDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("documentsDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="documentsDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("documentsDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewDocumentDialog;
